import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { OctagonAlert } from 'lucide-react'

import styles from './TrialEndedGracePeriodModal.module.scss'

import graceHero from '@/assets/images/grace-hero.webp'
import { AddBillingInfoButton } from '@/components/AddBillingInfoButton'
import DialogWrapper from '@/components/Dialog'
import { useAnalytics } from '@/hooks/useAnalytics'
import {
  selectCurrentEnterprise,
  selectGracePeriodDaysLeft,
  selectGracePeriodEndsAt,
  selectSeatInfo,
  selectShouldShowGracePeriodModal,
  selectUserPlanType,
} from '@/store/features/user/userSlice'
import { PlanType } from '@/types'
import { EventName, PLATFORM_ADMIN_PORTAL } from '@/utils/eventDefinitions'
import { getPaidPlanLabel } from '@/utils/subscription'

// Keyed off the grace boundary, not `trial_ends_at` — the subscription endpoint resets that to 0 once the trial ends, defeating "show once per grace window".
const SEEN_KEY_PREFIX = 'gracePeriodModalSeen:'

const seenKey = (gracePeriodEndsAt: number | null | undefined): string | null =>
  gracePeriodEndsAt ? `${SEEN_KEY_PREFIX}${gracePeriodEndsAt}` : null

// Err toward showing the modal on read failure (privacy mode / quota) — missing the billing nudge is worse than showing it twice.
const hasSeenModal = (gracePeriodEndsAt: number | null | undefined): boolean => {
  const key = seenKey(gracePeriodEndsAt)
  if (!key) return true
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

const markSeen = (gracePeriodEndsAt: number | null | undefined): void => {
  const key = seenKey(gracePeriodEndsAt)
  if (!key) return
  try {
    localStorage.setItem(key, '1')
  } catch {
    // Ignore quota / privacy mode — worst case the modal shows again next visit.
  }
}

// Drop stale `gracePeriodModalSeen:*` entries from previous trial/grace cycles so localStorage doesn't accumulate keys indefinitely.
const pruneStaleSeenKeys = (currentGracePeriodEndsAt: number | null | undefined): void => {
  const currentKey = seenKey(currentGracePeriodEndsAt)
  try {
    const toRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(SEEN_KEY_PREFIX) && k !== currentKey) toRemove.push(k)
    }
    toRemove.forEach((k) => localStorage.removeItem(k))
  } catch {
    // Ignore — pruning is best-effort housekeeping.
  }
}

// One-shot modal shown to admins on Home once the team trial has ended and they're inside the grace window. Self-contained, mirrors the `<UpgradePrompt />` pattern.
export const TrialEndedGracePeriodModal = () => {
  const shouldShow = useSelector(selectShouldShowGracePeriodModal)
  const gracePeriodEndsAt = useSelector(selectGracePeriodEndsAt)
  const planType = useSelector(selectUserPlanType) ?? PlanType.Pro
  const enterprise = useSelector(selectCurrentEnterprise)
  const seatInfo = useSelector(selectSeatInfo)
  const planLabel = getPaidPlanLabel(planType, enterprise?.subscription?.tier ?? seatInfo?.tier) ?? planType
  const daysLeft = useSelector(selectGracePeriodDaysLeft)
  const { trackEvent } = useAnalytics()

  const [isOpen, setIsOpen] = useState(false)
  // Guards `TeamBillingModalOpened` against double-firing when `trackEvent` identity flips after PostHog finishes loading.
  const hasTrackedOpenRef = useRef(false)

  useEffect(() => {
    pruneStaleSeenKeys(gracePeriodEndsAt)
  }, [gracePeriodEndsAt])

  // We deliberately do NOT call `markSeen` on open — see `close` below. Marking on render would (a) trip React 18 Strict Mode's double-effect, and (b) commit to "shown" before the user can interact.
  useEffect(() => {
    if (!shouldShow) {
      // Defensive close if conditions flip false while open (e.g. user completes checkout from the CTA). Mark seen to avoid a reopen flicker if `shouldShow` transiently flips back true before the grace window truly ends.
      if (isOpen) {
        markSeen(gracePeriodEndsAt)
        setIsOpen(false)
      }
      return
    }
    if (isOpen) return
    if (hasSeenModal(gracePeriodEndsAt)) return
    setIsOpen(true)
  }, [shouldShow, gracePeriodEndsAt, isOpen])

  useEffect(() => {
    if (!isOpen) {
      hasTrackedOpenRef.current = false
      return
    }
    if (hasTrackedOpenRef.current) return
    if (!trackEvent) return
    trackEvent(EventName.TeamBillingModalOpened, {
      source: 'grace_period_modal',
      platform: PLATFORM_ADMIN_PORTAL,
    })
    hasTrackedOpenRef.current = true
  }, [isOpen, trackEvent])

  if (!isOpen) return null

  const dayWord = daysLeft === 1 ? 'day' : 'days'
  const close = () => {
    markSeen(gracePeriodEndsAt)
    setIsOpen(false)
  }

  return (
    <DialogWrapper
      isOpen={isOpen}
      title={
        <>
          <OctagonAlert size={24} aria-hidden /> Your {planLabel} Trial has ended
        </>
      }
      subtitle={`Add billing info within ${daysLeft} ${dayWord} to keep access to your team and ${planLabel} features.`}
      close={close}
      minWidth={480}
      topMedia={<img src={graceHero} alt="" />}
    >
      <div className={styles.actions}>
        <AddBillingInfoButton title="Add billing info" type="dark" />
      </div>
    </DialogWrapper>
  )
}
